import axios from "common/configs/axiosConfigs";
import React, { useEffect, useState } from "react";
import CreateContactForm from "./CreateContactForm";
import Select from "common/components/Select";
import ImageUploader from "common/components/ImageUploader";
import { FURNISHED_OPTIONS, OPTIONS } from "common/constants/enums";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const CreateProperty = ({ property }) => {
  const companyId = JSON.parse(localStorage.getItem("user-auth"))?.company_id;
  const navigate = useNavigate();
  const [values, setValues] = useState({});
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [uniqueFeatures, setUniqueFeatures] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const AMENITIES_OPTIONS = amenities?.map((o) => ({ value: o?.id, label: o?.name }));
  const UNIQUE_FEATURES = uniqueFeatures?.map((o) => ({ value: o?.id, label: o?.name }));
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [invalidStep, setInvalidStep] = useState(property ? 7 : 1);
  const [formDataFiles] = useState(new FormData());
  const [propertyTypeTrack, setPropertyTypeTrack] = useState([]);

  const [formData, setFormData] = useState({
    step1: {},
    step2: {},
    step3: {},
    step4: {},
    step5: {},
    step6: {},
  });

  const propertySubTypes = propertyTypes?.subtype?.filter(
    (type) => formData?.step2?.propertyType?.toString() === type?.property_type_id.toString()
  );
  const contactOptions = contacts?.map((c) => ({
    label: `${c?.first_name} ${c?.last_name} - ${c?.phone}`,
    value: c?.id,
  }));

  const FORM_DATA = {
    step1: { contactId: property?.contactId || "" },
    step2: {
      saleType: property?.saleType || "",
      propertyType: property?.propertyType?.toString() || "",
      propertySubtype: property?.propertySubtype?.toString() || "r",
      transactionType: property?.transactionType || "",
    },
    step3: {
      ...(formData?.step2?.propertyType?.toString() === "1" && {
        bedroom: property?.bedroom || "",
        bathroom: property?.bathroom?.toString() || "",
      }),
      ...(formData?.step2?.propertyType?.toString() === "2" && {
        personalWashroom: property?.personalWashroom || "",
        pantry: property?.pantry || "",
      }),
      parkings: property?.parkings?.toString() || "",
      furnished: property?.furnished || "",
      propertyAge: property?.propertyAge?.toString() || "",
      occupancyCertificate: property?.occupancyCertificate || "",
      uniqueFeatures: property?.uniqueFeatures || [],
      amenities: property?.amenities || [],
      description: property?.description || "",
    },
    step4: {
      propertyAddress: property?.propertyAddress || "",
      propertyName: property?.propertyName || "",
      propertyTitle: property?.propertyTitle || "",
      unitNumber: property?.unitNumber || "",
      street: property?.street || "",
      landmark: property?.landmark || "",
      pincode: property?.pincode || "",
      city: property?.city || "",
      latitude: "0",
      longitude: "0",
      locality: property?.locality || "",
    },
    step5: {
      price: property?.price || "",
      negotiableAmount: property?.negotiableAmount || "",
      rate: property?.rate || "",
      maintenance: property?.maintenance || "",
      deposit: property?.deposit || "",
      coverArea: property?.coverArea || "",
      buildupArea: property?.buildupArea || "",
      plotArea: property?.plotArea || "",
      carpetArea: property?.carpetArea || "",
      gardenArea: property?.gardenArea || "",
      terraceArea: property?.terraceArea || "",
      plotDimension: property?.plotDimension || "",
      propertyDimension: property?.propertyDimension || "",
    },
    step6: {
      youtubeLink: property?.youtubeLink || "",
      propertyImages: [],
    },
  };
  useEffect(() => {
    setFormData(FORM_DATA);
    // eslint-disable-next-line
  }, [property]);
  useEffect(() => {
    for (let step = currentStep; step <= 6; step++) {
      if (!isStepValid(step)) {
        setInvalidStep(step);
        break; // Exit the loop once the first invalid step is found
      } else {
        setInvalidStep(6);
      }
    }
  }, [formData]);

  const handleFormInput = ({ target: { name, value, isMulti, files } }) => {
    setFormData((prev) => ({
      ...prev,
      [`step${currentStep}`]: {
        ...prev[`step${currentStep}`],
        [name]: files?.[0] || value,
      },
    }));

    // name !== "propertyImages" && formDataFiles.set(name, isMulti ? JSON.stringify(value) : value); // Use .set() to overwrite values if they exist
  };

  const getAmenities = async () => {
    try {
      const { data } = await axios.post("", {
        action: "getPropertyAmenities",
        companyId,
      });
      if (data?.success) {
        setAmenities(data?.data);
      } else {
        toast.error(data?.error);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const isStepValid = (step) => {
    const optionals = ["youtubeLink"];
    const currentStepData = formData?.[`step${step || currentStep}`];
    const currentStepFields = Object.keys(currentStepData);

    return currentStepFields.every((field) => {
      if (optionals.includes(field)) return true;

      return Array.isArray(currentStepData[field])
        ? currentStepData[field].length
        : currentStepData[field];
    });
  };

  const nextStep = () => {
    setCurrentStep(currentStep + 1);
  };
  const handleOpenStep = (step) => {
    setCurrentStep(step);
  };

  const previousStep = () => {
    setCurrentStep(currentStep - 1);
  };
  const onClose = () => {
    setIsOpen(false);
  };
  const handleChange = ({ target: { name, value } }) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const getPropertyTypes = async () => {
    try {
      const { data } = await axios.post("", { action: "getPropertyType" });
      if (data.success) {
        setPropertyTypes(data);
      }
    } catch (err) {
      console.error("ERROR:", err);
    }
  };
  const getUniqueProperties = async () => {
    try {
      const { data } = await axios.post("", { action: "getUniqueFeatures" });

      if (data.success) {
        setUniqueFeatures(data?.data);
      }
    } catch (err) {
      console.error("ERROR:", err);
    }
  };
  const getContacts = async (isNew) => {
    try {
      const { data } = await axios.post("", { action: "getContact", companyId });
      if (data.success) {
        setContacts(data?.data);
        isNew &&
          setFormData((prev) => {
            const formDataCopy = { ...prev };
            formDataCopy.step1.contactId = data?.data?.at(-1)?.id;

            return formDataCopy;
          });
        isNew && setValues((v) => ({ ...v, contactId: data?.data?.at(-1)?.id }));
      }
    } catch (err) {
      console.error("ERROR:", err);
    }
  };
  useEffect(() => {
    getPropertyTypes();
    getUniqueProperties();
    getContacts();
    getAmenities();
  }, []);

  useEffect(() => {
    if (propertyTypeTrack.at(-2)) {
      setFormData((prev) => {
        const formDataCopy = { ...prev };
        formDataCopy.step2.propertySubType = "";

        return formDataCopy;
      });
    }
  }, [propertyTypeTrack]);
  useEffect(() => {
    setPropertyTypeTrack((prev) => [...prev, formData?.step2?.propertyType]);

    setFormData((prevForm) => {
      const formDataCopy = { ...prevForm };

      if (formData?.step2?.propertyType?.toString() === "1") {
        if (property) {
          formDataCopy.step3.bedroom = property?.bedroom;
          formDataCopy.step3.bathroom = property?.bathroom;
        }
        delete formDataCopy?.step3?.personalWashroom;
        delete formDataCopy?.step3?.pantry;
      }
      if (formData?.step2?.propertyType?.toString() === "2") {
        if (property) {
          formDataCopy.step3.personalWashroom = property?.personal_washroom;
          formDataCopy.step3.pantry = property?.pantry;
        }
        delete formDataCopy?.step3?.bedroom;
        delete formDataCopy?.step3?.balconies;
      }
      return formDataCopy;
    });
  }, [formData?.step2?.propertyType]);

  const handleSubmitProperty = async (e) => {
    e.preventDefault();
    formDataFiles.set("action", property ? "editVilla" : "addVilla");
    formDataFiles.set("companyId", companyId);
    formDataFiles.set("latitude", 0);
    formDataFiles.set("longitude", 0);
    formDataFiles.set("villaId", property?.id);

    // Loop over the nested objects and append each key-value pair to FormData
    Object.values(formData).forEach((step) => {
      Object.entries(step).forEach(([key, value]) => {
        key === "propertyImages"
          ? value.forEach((file) => {
              formDataFiles.append(`${key}[]`, file);
            })
          : formDataFiles.set(key, Array.isArray(value) ? JSON.stringify(value) : value);
      });
    });
    try {
      const { data } = await axios.post("", formDataFiles, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (data?.success) {
        toast.success(data?.msg);
        navigate("/villas");
      } else {
        toast.error(data?.error);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setFormData(FORM_DATA);
    }
  };
  const [thumbs, setThumbs] = useState([]);

  const monitorThumbs = (t) => setThumbs(t);
  return (
    <>
      <section className="create-property-section">
        <h4 className="page-title">{property ? "Edit Villa" : "Create Villa"}</h4>
        <form onSubmit={handleSubmitProperty}>
          <div className={`card mt-2 `}>
            <div
              onClick={() => handleOpenStep(1)}
              className={`d-flex justify-content-between align-items-center ${
                invalidStep >= 1 ? "cursor-pointer" : "step-disabled"
              }`}
            >
              <div>
                <small className="text-muted">Step 1</small>
                <h6>CONTACT INFORMATION</h6>
              </div>

              <i className="bi bi-chevron-down fs-20"></i>
            </div>
            <div className={`step ${currentStep === 1 ? "d-flex" : "d-none"}`}>
              <div className="w-50">
                <Select
                  options={contactOptions}
                  name="contactId"
                  value={values.contactId || formData?.step1?.contactId}
                  onChange={(e) => {
                    handleChange(e);
                    handleFormInput(e);
                  }}
                  placeholder="Owner/Landlord"
                />
              </div>
              <button type="button" className="btn btn-dark ms-3" onClick={() => setIsOpen(true)}>
                Create Contact
              </button>
            </div>
            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="ms-3 btn btn-primary"
                onClick={nextStep}
                disabled={!isStepValid()}
              >
                Next
              </button>
            </div>
          </div>

          <div className={`card mt-2`}>
            <div
              onClick={() => handleOpenStep(2)}
              className={`d-flex justify-content-between align-items-center ${
                invalidStep >= 2 ? "cursor-pointer" : "step-disabled"
              }`}
            >
              <div>
                <small className="text-muted">Step 2</small>
                <h6>PROPERTY SELLING INFO</h6>
              </div>

              <i className="bi bi-chevron-down fs-20"></i>
            </div>
            <div className={`step row gy-4 ${currentStep === 2 ? "" : "d-none"}`}>
              <div className="col-12">
                <span className="label">Property for</span>
                <div className="row radio-btn-group g-3">
                  <label>
                    <input
                      type="radio"
                      name="saleType"
                      value="rent"
                      onChange={handleFormInput}
                      className="d-none"
                      checked={formData?.step2?.saleType === "rent"}
                    />
                    <span className="radio-btn">Rent</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="saleType"
                      value="sale"
                      onChange={handleFormInput}
                      className="d-none"
                      checked={formData?.step2?.saleType === "sale"}
                    />
                    <span className="radio-btn">Sale</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="saleType"
                      value="lease"
                      onChange={handleFormInput}
                      className="d-none"
                      checked={formData?.step2?.saleType === "lease"}
                    />
                    <span className="radio-btn">Lease</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="saleType"
                      value="pg"
                      onChange={handleFormInput}
                      className="d-none"
                      checked={formData?.step2?.saleType === "pg"}
                    />
                    <span className="radio-btn">PG</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="saleType"
                      value="redevelopment"
                      onChange={handleFormInput}
                      className="d-none"
                      checked={formData?.step2?.saleType === "redevelopment"}
                    />
                    <span className="radio-btn">Re-development</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="saleType"
                      value="services"
                      onChange={handleFormInput}
                      className="d-none"
                      checked={formData?.step2?.saleType?.toString() === "services"}
                    />
                    <span className="radio-btn">Services</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="saleType"
                      value="jointventures"
                      onChange={handleFormInput}
                      className="d-none"
                      checked={formData?.step2?.saleType?.toString() === "jointventures"}
                    />
                    <span className="radio-btn">Joint ventures</span>
                  </label>
                </div>
              </div>
              <div className="col-12">
                <span className="label">Property type</span>
                <div className="row radio-btn-group g-3">
                  {propertyTypes?.data
                    ?.filter((a) => a?.id === 1)
                    .map((type) => (
                      <label>
                        <input
                          type="radio"
                          name="propertyType"
                          value={type?.id}
                          onChange={(e) => {
                            handleFormInput(e);
                            setFormData((prev) => ({
                              ...prev,
                              step2: {
                                ...prev?.step2,
                                propertySubType: property?.property_subtype || "",
                              },
                            }));
                          }}
                          className="d-none"
                          checked={
                            formData?.step2?.propertyType?.toString() === type?.id?.toString()
                          }
                        />
                        <span className="radio-btn">{type?.name}</span>
                      </label>
                    ))}
                </div>
              </div>
              {!!propertySubTypes?.length && (
                <div className="col-12">
                  <span className="label">Property sub type</span>
                  <div className="row radio-btn-group g-3">
                    {propertySubTypes?.map((type) => (
                      <label>
                        <input
                          type="radio"
                          name="propertySubType"
                          value={type?.id}
                          onChange={handleFormInput}
                          className="d-none"
                          checked={
                            formData?.step2?.propertySubType?.toString() === type?.id?.toString()
                          }
                        />
                        <span className="radio-btn">{type?.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="col-12">
                <span className="label">Property transaction type</span>
                <div className="row radio-btn-group g-3">
                  {propertyTypes?.transaction_type?.map((type) => (
                    <label>
                      <input
                        type="radio"
                        name="transactionType"
                        value={type?.id}
                        onChange={handleFormInput}
                        className="d-none"
                        checked={formData?.step2?.transactionType === type?.id?.toString()}
                      />
                      <span className="radio-btn">{type?.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <button type="button" className="btn btn-light" onClick={previousStep}>
                Back
              </button>
              <button
                type="button"
                className="ms-3 btn btn-primary"
                onClick={nextStep}
                disabled={!isStepValid()}
              >
                Next
              </button>
            </div>
          </div>
          <div className={`card mt-2 `}>
            <div
              onClick={() => handleOpenStep(3)}
              className={`d-flex justify-content-between align-items-center ${
                invalidStep >= 3 ? "cursor-pointer" : "step-disabled"
              }`}
            >
              <div>
                <small className="text-muted">Step 3</small>
                <h6>
                  PROPERTY FEATURE{" "}
                  <span className="text-primary text-uppercase">
                    {
                      propertyTypes?.data?.find(
                        (type) => type?.id?.toString() === formData?.step2?.propertyType?.toString()
                      )?.name
                    }
                  </span>
                </h6>
              </div>

              <i className="bi bi-chevron-down fs-20"></i>
            </div>
            <div className={`step row gy-4 ${currentStep === 3 ? "" : "d-none"}`}>
              {formData?.step2?.propertyType?.toString() === "1" && (
                <>
                  <div className="col-lg-4 col-md-6">
                    <span className="label">Bedrooms</span>
                    <Select
                      options={OPTIONS}
                      name="bedroom"
                      value={formData.step3.bedroom}
                      onChange={handleFormInput}
                      placeholder="Bedrooms"
                    />
                  </div>
                  <div className="col-lg-4 col-md-6">
                    <span className="label">Bathroom</span>
                    <Select
                      options={OPTIONS}
                      name="bathroom"
                      value={formData.step3.bathroom}
                      onChange={handleFormInput}
                      placeholder="Bathroom"
                    />
                  </div>
                </>
              )}
              {formData?.step2?.propertyType?.toString() === "2" && (
                <>
                  <div className="col-lg-4 col-md-6">
                    <span className="label">Personal Washroom</span>
                    <Select
                      options={OPTIONS}
                      name="personalWashroom"
                      value={formData.step3.personalWashroom}
                      onChange={handleFormInput}
                      placeholder="Personal washroom"
                    />
                  </div>
                  <div className="col-lg-4 col-md-6">
                    <span className="label">Pantry / Cafeteria</span>
                    <Select
                      options={[
                        { value: "yes", label: "Yes" },
                        { value: "no", label: "No" },
                      ]}
                      name="pantry"
                      value={formData.step3.pantry}
                      onChange={handleFormInput}
                      placeholder="Pantry / Cafeteria"
                    />
                  </div>
                </>
              )}

              <div className="col-lg-4 col-md-6">
                <span className="label">Parkings</span>
                <Select
                  options={OPTIONS}
                  name="parkings"
                  value={formData.step3.parkings}
                  onChange={handleFormInput}
                  placeholder="Number of Parkings"
                />
              </div>

              <div className="col-lg-4 col-md-6">
                <span className="label">Furnished Status</span>
                <Select
                  options={FURNISHED_OPTIONS}
                  name="furnished"
                  value={formData.step3.furnished}
                  onChange={handleFormInput}
                  placeholder="Furnished status"
                />
              </div>
              <div className="col-lg-4 col-md-6">
                <span className="label">Age of Property</span>
                <Select
                  options={OPTIONS}
                  name="propertyAge"
                  value={formData.step3.propertyAge}
                  onChange={handleFormInput}
                  placeholder="Age of Property"
                />
              </div>
              <div className="col-lg-4 col-md-6">
                <span className="label">Occupancy Certificate</span>
                <Select
                  options={[
                    { value: "yes", label: "Yes" },
                    { value: "no", label: "No" },
                  ]}
                  name="occupancyCertificate"
                  value={formData.step3.occupancyCertificate}
                  onChange={handleFormInput}
                  placeholder="Occupancy Certificate "
                />
              </div>
              <div className="col-lg-6 ">
                <span className="label">Unique Features</span>
                <Select
                  options={UNIQUE_FEATURES}
                  isMulti={true}
                  name="uniqueFeatures"
                  value={formData.step3.uniqueFeatures}
                  onChange={handleFormInput}
                  placeholder="Unique features"
                />
              </div>
              <div className="col-lg-6">
                <span className="label">Amenities</span>
                <Select
                  options={AMENITIES_OPTIONS}
                  isMulti={true}
                  name="amenities"
                  value={formData.step3.amenities}
                  onChange={handleFormInput}
                  placeholder="Amenities "
                />
              </div>
              <div className="col-lg-12">
                <span className="label">Description</span>
                <textarea
                  className="form-control"
                  rows={4}
                  name="description"
                  value={formData.step3.description}
                  onChange={handleFormInput}
                  placeholder="Description"
                ></textarea>
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <button type="button" className="btn btn-light" onClick={previousStep}>
                Back
              </button>
              <button
                type="button"
                className="ms-3 btn btn-primary"
                onClick={nextStep}
                disabled={!isStepValid()}
              >
                Next
              </button>
            </div>
          </div>
          <div className={`card mt-2 `}>
            <div
              onClick={() => handleOpenStep(4)}
              className={`d-flex justify-content-between align-items-center ${
                invalidStep >= 4 ? "cursor-pointer" : "step-disabled"
              }`}
            >
              <div>
                <small className="text-muted">Step 4</small>
                <h6>PROPERTY LOCATION</h6>
              </div>

              <i className="bi bi-chevron-down fs-20"></i>
            </div>
            <div className={`step row gy-4 ${currentStep === 4 ? "" : "d-none"}`}>
              <div className="col-lg-12">
                <label>
                  <span className="label">Address</span>
                  <input
                    type="text"
                    className="form-control"
                    name="propertyAddress"
                    value={formData.step4.propertyAddress}
                    onChange={handleFormInput}
                    placeholder="Full address"
                  />
                </label>
              </div>

              <div className="col-lg-6">
                <label>
                  <span className="label">Latitude</span>
                  <input
                    type="text"
                    className="form-control"
                    readOnly
                    name="latitude"
                    value={formData.step4.latitude}
                    onChange={handleFormInput}
                    placeholder="Latitude"
                  />
                </label>
              </div>
              <div className="col-lg-6">
                <label>
                  <span className="label">Longitude</span>
                  <input
                    type="text"
                    className="form-control"
                    name="longitude"
                    value={formData.step4.longitude}
                    onChange={handleFormInput}
                    readOnly
                    placeholder="Longitude"
                  />
                </label>
              </div>
              <div className="col-lg-12">
                <label>
                  <span className="label">Property Display Title</span>
                  <input
                    type="text"
                    className="form-control"
                    name="propertyTitle"
                    value={formData.step4.propertyTitle}
                    onChange={handleFormInput}
                    placeholder="Property Display Title"
                  />
                </label>
              </div>
              <div className="col-lg-12">
                <label>
                  <span className="label">Property name / Developer name</span>
                  <input
                    type="text"
                    className="form-control"
                    name="propertyName"
                    value={formData.step4.propertyName}
                    onChange={handleFormInput}
                    placeholder="Name"
                  />
                </label>
              </div>
              <div className="col-lg-6">
                <label>
                  <span className="label">Flat / Office / Unit no.</span>
                  <input
                    type="text"
                    name="unitNumber"
                    value={formData.step4.unitNumber}
                    onChange={handleFormInput}
                    className="form-control"
                    placeholder="Flat / Office / Unit no."
                  />
                </label>
              </div>

              <div className="col-lg-6">
                <label>
                  <span className="label">Street</span>
                  <input
                    type="text"
                    className="form-control"
                    name="street"
                    value={formData.step4.street}
                    onChange={handleFormInput}
                    placeholder="Street"
                  />
                </label>
              </div>
              <div className="col-lg-6">
                <label>
                  <span className="label">Landmark</span>
                  <input
                    type="text"
                    className="form-control"
                    name="landmark"
                    value={formData.step4.landmark}
                    onChange={handleFormInput}
                    placeholder="Landmark"
                  />
                </label>
              </div>
              <div className="col-lg-6">
                <label>
                  <span className="label">Pincode number</span>
                  <input
                    type="number"
                    className="form-control"
                    pattern="\d{6}"
                    name="pincode"
                    value={formData.step4.pincode}
                    onChange={handleFormInput}
                    placeholder="Pincode number"
                  />
                </label>
              </div>
              <div className="col-lg-6">
                <label>
                  <span className="label">City</span>
                  <input
                    type="text"
                    className="form-control"
                    name="city"
                    value={formData.step4.city}
                    onChange={handleFormInput}
                    placeholder="City"
                  />
                </label>
              </div>
              <div className="col-lg-6">
                <label>
                  <span className="label">Locality</span>
                  <input
                    type="text"
                    className="form-control"
                    name="locality"
                    value={formData.step4.locality}
                    onChange={handleFormInput}
                    placeholder="Locality"
                  />
                </label>
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <button type="button" className="btn btn-light" onClick={previousStep}>
                Back
              </button>
              <button
                type="button"
                className="ms-3 btn btn-primary"
                onClick={nextStep}
                disabled={!isStepValid()}
              >
                Next
              </button>
            </div>
          </div>
          <div className="card mt-2">
            <div
              onClick={() => handleOpenStep(5)}
              className={`d-flex justify-content-between align-items-center ${
                invalidStep >= 5 ? "cursor-pointer" : "step-disabled"
              }`}
            >
              <div>
                <small className="text-muted">Step 5</small>
                <h6>AREA & PRICING DETAILS</h6>
              </div>

              <i className="bi bi-chevron-down fs-20"></i>
            </div>
            <div className={`step row gy-4 ${currentStep === 5 ? "" : "d-none"}`}>
              <div className="col-lg-6">
                <label>
                  <span className="label">Cover area (sqft)</span>
                  <input
                    type="number"
                    className="form-control"
                    name="coverArea"
                    value={formData.step5.coverArea}
                    onChange={handleFormInput}
                    placeholder="Cover area"
                  />
                </label>
              </div>
              <div className="col-lg-6">
                <label>
                  <span className="label">Buildup area (sqft)</span>
                  <input
                    type="number"
                    className="form-control"
                    name="buildupArea"
                    value={formData.step5.buildupArea}
                    onChange={handleFormInput}
                    placeholder="Buildup area"
                  />
                </label>
              </div>
              <div className="col-lg-6">
                <label>
                  <span className="label">Plot area (sqft)</span>
                  <input
                    type="number"
                    className="form-control"
                    name="plotArea"
                    value={formData.step5.plotArea}
                    onChange={handleFormInput}
                    placeholder="Plot area"
                  />
                </label>
              </div>
              <div className="col-lg-6">
                <label>
                  <span className="label">Carpet area (sqft)</span>
                  <input
                    type="number"
                    className="form-control"
                    name="carpetArea"
                    value={formData.step5.carpetArea}
                    onChange={handleFormInput}
                    placeholder="Carpet area"
                  />
                </label>
              </div>
              <div className="col-lg-6">
                <label>
                  <span className="label">Garden area (sqft)</span>
                  <input
                    type="number"
                    className="form-control"
                    name="gardenArea"
                    value={formData.step5.gardenArea}
                    onChange={handleFormInput}
                    placeholder="Garden area"
                  />
                </label>
              </div>
              <div className="col-lg-6">
                <label>
                  <span className="label">Terrace area (sqft)</span>
                  <input
                    type="number"
                    className="form-control"
                    name="terraceArea"
                    value={formData.step5.terraceArea}
                    onChange={handleFormInput}
                    placeholder="Terrace area"
                  />
                </label>
              </div>
              <div className="col-lg-6">
                <label>
                  <span className="label">Plot Dimension</span>
                  <input
                    type="text"
                    className="form-control"
                    name="plotDimension"
                    value={formData.step5.plotDimension}
                    onChange={handleFormInput}
                    placeholder="Plot Dimension"
                  />
                </label>
              </div>
              <div className="col-lg-6">
                <label>
                  <span className="label">Property Dimension</span>
                  <input
                    type="text"
                    className="form-control"
                    name="propertyDimension"
                    value={formData.step5.propertyDimension}
                    onChange={handleFormInput}
                    placeholder="Property Dimension"
                  />
                </label>
              </div>
              <div className="col-lg-6">
                <label>
                  <span className="label">Asking price</span>
                  <input
                    type="number"
                    className="form-control"
                    name="price"
                    value={formData.step5.price}
                    onChange={handleFormInput}
                    placeholder="Asking price"
                  />
                </label>
              </div>
              <div className="col-lg-6">
                <label>
                  <span className="label">Negotiable price</span>
                  <input
                    type="number"
                    className="form-control"
                    name="negotiableAmount"
                    value={formData.step5.negotiableAmount}
                    onChange={handleFormInput}
                    placeholder="Negotiable Amount"
                  />
                </label>
              </div>
              <div className="col-lg-6">
                <label>
                  <span className="label">Rate per Sq.Ft</span>
                  <input
                    type="number"
                    className="form-control"
                    name="rate"
                    value={formData.step5.rate}
                    onChange={handleFormInput}
                    placeholder="Rate"
                  />
                </label>
              </div>
              <div className="col-lg-6">
                <label>
                  <span className="label">Maintenance</span>
                  <input
                    type="number"
                    className="form-control"
                    name="maintenance"
                    value={formData.step5.maintenance}
                    onChange={handleFormInput}
                    placeholder="Maintenance"
                  />
                </label>
              </div>
              <div className="col-lg-6">
                <label>
                  <span className="label">Deposit</span>
                  <input
                    type="number"
                    className="form-control"
                    name="deposit"
                    value={formData.step5.deposit}
                    onChange={handleFormInput}
                    placeholder="Deposit"
                  />
                </label>
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <button type="button" className="btn btn-light" onClick={previousStep}>
                Back
              </button>
              <button
                type="button"
                className="ms-3 btn btn-primary"
                onClick={nextStep}
                disabled={!isStepValid()}
              >
                Next
              </button>
            </div>
          </div>
          <div className="card mt-2">
            <div
              onClick={() => handleOpenStep(6)}
              className={`d-flex justify-content-between align-items-center ${
                invalidStep >= 6 ? "cursor-pointer" : "step-disabled"
              }`}
            >
              <div>
                <small className="text-muted">Step 6</small>
                <h6>PROPERTY PHOTOS & YOUTUBE LINK</h6>
              </div>

              <i className="bi bi-chevron-down fs-20"></i>
            </div>
            <div className={`step  ${currentStep === 6 ? "d-block" : "d-none"}`}>
              <ImageUploader
                defaultPreview={property?.image_paths}
                defaultThumbnail={property?.property_thumbnail}
                name="propertyImages"
                thumbnail={formData.step6.thumbnail}
                value={formData.step6.propertyImages}
                onChange={handleFormInput}
                formDataFiles={formDataFiles}
                monitorThumbs={monitorThumbs}
              />
              <label>
                <span className="label">Youtube URL (Optional)</span>
                <input
                  type="text"
                  name="youtubeLink"
                  value={formData.step6.youtubeLink}
                  onChange={handleFormInput}
                  className="form-control"
                  placeholder="Youtube URL"
                />
              </label>
            </div>
            <div className="d-flex justify-content-end">
              <button type="button" className="btn btn-light" onClick={previousStep}>
                Back
              </button>
              <button
                type="submit"
                className="ms-3 btn btn-primary"
                disabled={!(property?.property_thumbnail || formData.step6.thumbnail)}
              >
                Submit
              </button>
            </div>
          </div>
        </form>
      </section>
      <CreateContactForm {...{ isOpen, onClose, getContacts }} />
    </>
  );
};

export default CreateProperty;
